import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Loader2, Mail, Phone, MessageSquare } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputClass =
  'w-full bg-transparent border border-black/15 focus:border-gold focus:outline-none px-4 py-3 text-foreground placeholder:text-foreground/40 transition-colors';

const ContactPage = () => {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [branding, setBranding] = useState({ contact_email: '', phone: '' });

  useEffect(() => {
    axios
      .get(`${API}/settings`)
      .then((res) => {
        const b = res.data?.branding;
        if (b) {
          setBranding({
            contact_email: b.contact_email || '',
            phone: b.phone || '',
          });
        }
      })
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success('Votre message a bien été envoyé. Nous vous répondrons vite.');
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de l'envoi");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-24" data-testid="contact-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-3xl mb-16"
        >
          <p className="text-sm uppercase tracking-widest text-gold mb-2">Nous contacter</p>
          <h1 className="font-heading text-4xl md:text-6xl tracking-tight mb-6">
            Une question, un projet ?
          </h1>
          <p className="text-foreground/70 leading-relaxed mb-4">
            Une question, un projet ou une demande particulière ?
          </p>
          <p className="text-foreground/60 leading-relaxed mb-4">
            Notre équipe est à votre écoute pour vous accompagner dans vos demandes concernant nos
            créations, les commandes sur mesure, les collaborations, les partenariats ou
            l'organisation d'événements.
          </p>
          <p className="text-foreground/60 leading-relaxed">
            Nous serons ravis d'échanger avec vous et de vous faire découvrir l'univers de MAISON KA.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.15 }}
            className="lg:col-span-2 space-y-8"
          >
            <div>
              <h2 className="font-heading text-xl text-gold mb-6">Contactez-nous</h2>
              <ul className="space-y-5">
                <li className="flex items-start gap-4 text-foreground/70">
                  <Mail size={18} className="text-gold mt-1 shrink-0" />
                  <div>
                    <p className="text-sm uppercase tracking-widest text-foreground/50 mb-1">Par e-mail</p>
                    {branding.contact_email ? (
                      <a href={`mailto:${branding.contact_email}`} className="hover:text-gold transition-colors" data-testid="contact-email-link">
                        {branding.contact_email}
                      </a>
                    ) : (
                      <span>Via le formulaire ci-contre</span>
                    )}
                  </div>
                </li>
                <li className="flex items-start gap-4 text-foreground/70">
                  <Phone size={18} className="text-gold mt-1 shrink-0" />
                  <div>
                    <p className="text-sm uppercase tracking-widest text-foreground/50 mb-1">Par téléphone</p>
                    {branding.phone ? (
                      <a href={`tel:${branding.phone.replace(/\s/g, '')}`} className="hover:text-gold transition-colors" data-testid="contact-phone-link">
                        {branding.phone}
                      </a>
                    ) : (
                      <span>Sur demande via le formulaire</span>
                    )}
                  </div>
                </li>
                <li className="flex items-start gap-4 text-foreground/70">
                  <MessageSquare size={18} className="text-gold mt-1 shrink-0" />
                  <div>
                    <p className="text-sm uppercase tracking-widest text-foreground/50 mb-1">Formulaire</p>
                    <span>Remplissez le formulaire — nous vous répondrons dans les meilleurs délais.</span>
                  </div>
                </li>
              </ul>
            </div>

            <p className="text-foreground/40 text-sm italic border-t border-black/10 pt-8">
              MAISON KA — L'élégance d'un héritage en mouvement.
            </p>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            onSubmit={submit}
            className="lg:col-span-3 space-y-5"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <input
                className={inputClass}
                placeholder="Votre nom *"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                data-testid="contact-name"
              />
              <input
                type="email"
                className={inputClass}
                placeholder="Votre email *"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                data-testid="contact-email"
              />
            </div>
            <input
              className={inputClass}
              placeholder="Sujet"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              data-testid="contact-subject"
            />
            <textarea
              className={`${inputClass} min-h-[180px]`}
              placeholder="Votre message *"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              required
              data-testid="contact-message"
            />
            <button type="submit" disabled={sending} className="btn-primary inline-flex items-center gap-2" data-testid="contact-submit">
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Envoyer
            </button>
          </motion.form>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
